import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const BRAND_GRADIENT =
  "linear-gradient(135deg, #5f2eea, #0a84ff)";

const BASE_URL = "http://165.22.218.70:1112";

/* ================= WRAPPER ================= */

const Wrapper = styled.div`
  min-height: 100vh;
  padding: 60px 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  font-family: "Poppins", sans-serif;
`;

/* ================= CARD ================= */

const Card = styled(motion.div)`
  width: 100%;
  max-width: 600px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(14px);
  border-radius: 20px;
  padding: 40px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

/* ================= TITLE ================= */

const Title = styled.h2`
  text-align: center;
  margin-bottom: 30px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

/* ================= FORM ================= */

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #e5e7eb;
  display: block;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(255,255,255,0.12);
  color: #fff;
  outline: none;

  &::placeholder {
    color: #cbd5e1;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(255,255,255,0.12);
  color: #fff;
  outline: none;

  option {
    color: #000;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(255,255,255,0.12);
  color: #fff;
  resize: none;
  outline: none;

  &::placeholder {
    color: #cbd5e1;
  }
`;

const Button = styled(motion.button)`
  width: 100%;
  padding: 14px;
  border-radius: 30px;
  border: none;
  background: ${BRAND_GRADIENT};
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
`;

const Success = styled.p`
  margin-top: 20px;
  text-align: center;
  color: #4ade80;
  font-weight: 600;
`;

const Error = styled.p`
  margin-top: 20px;
  text-align: center;
  color: #ff6b6b;
  font-weight: 600;
`;

/* ================= COMPONENT ================= */

export default function ApplyLeave() {
  const [form, setForm] = useState({
    type: "",
    fromDate: "",
    toDate: "",
    reason: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(false);
    setError("");

    try {
      // ⚡ Assuming you store userId in localStorage after login
      const userId = localStorage.getItem("userId");

      if (!userId) {
        setError("User not logged in properly.");
        return;
      }

      const res = await fetch(`${BASE_URL}/webapp/leave`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user: {
            id: Number(userId),
          },
          startDate: form.fromDate,
          endDate: form.toDate,
          reason: form.reason,
        }),
      });

      if (!res.ok) {
        throw new Error("Failed to apply leave");
      }

      setSubmitted(true);

      setForm({
        type: "",
        fromDate: "",
        toDate: "",
        reason: "",
      });

    } catch (err) {
      setError("Failed to submit leave request.");
    }
  };

  return (
    <Wrapper>
      <Card
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Title>Apply for Leave</Title>

        <form onSubmit={handleSubmit}>
          <Label>Leave Type</Label>
          <Select
            name="type"
            value={form.type}
            onChange={handleChange}
            required
          >
            <option value="">Select Leave Type</option>
            <option>Sick Leave</option>
            <option>Casual Leave</option>
            <option>Paid Leave</option>
            <option>Emergency Leave</option>
          </Select>

          <Label>From Date</Label>
          <Input
            type="date"
            name="fromDate"
            value={form.fromDate}
            onChange={handleChange}
            required
          />

          <Label>To Date</Label>
          <Input
            type="date"
            name="toDate"
            value={form.toDate}
            onChange={handleChange}
            required
          />

          <Label>Reason</Label>
          <Textarea
            rows="4"
            name="reason"
            placeholder="Enter reason for leave..."
            value={form.reason}
            onChange={handleChange}
            required
          />

          <Button
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
          >
            Submit Leave Request
          </Button>
        </form>

        {submitted && (
          <Success>Leave request submitted successfully!</Success>
        )}

        {error && <Error>{error}</Error>}
      </Card>
    </Wrapper>
  );
}